import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import {
  DropdownInput,
  FilterBuilder,
  FilterBuilderFieldsType,
  FilterBuilderFilterType,
  Form,
  Radio,
  useForm,
  VariableTextInput,
} from "@validereinc/common-components";
import {
  ReportGeneratedStatus,
  ReportStatusV3,
  TemplatedReportStatus,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { UserGroupsDropdownInput } from "../../../../Dropdowns/UserGroupsDropdownInput";
import { UsersDropdownInput } from "../../../../Dropdowns/UsersDropdownInput";
import { parseBackendFilterToFilterBuilderFormat } from "../Utils/WorkflowConfigurationUpdateRecordStatusDetails.helpers";
import {
  SendReportFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationSendReportDetails.module.scss";

const cx = classNames.bind(styles);

export const SEND_REPORT_ACTION_FILTER_BUILDER_FIELDS: FilterBuilderFieldsType =
  {
    id: {
      data_type: "string",
      display_name: "Report ID",
    },
    name: {
      data_type: "string",
      display_name: "Report Name",
    },
    status: {
      data_type: "pick-list",
      display_name: "Report Status",
      pick_list_options: Object.values(ReportStatusV3),
    },
    templated_report_name: {
      data_type: "string",
      display_name: "Templated Report Name",
    },
    created_at: {
      data_type: "string",
      display_name: "Created At",
    },
    created_by: {
      data_type: "string",
      display_name: "Created By",
    },
    "templated_report.name": {
      data_type: "string",
      display_name: "Templated Report Name",
    },
    "templated_report.display_name": {
      data_type: "string",
      display_name: "Templated Report Display Name",
    },
    "templated_report.status": {
      data_type: "pick-list",
      pick_list_options: Object.values(TemplatedReportStatus),
      display_name: "Templated Report Status",
    },
    "templated_report.created_at": {
      data_type: "date",
      display_name: "Templated Report Created At",
      format: "yyyy-MM-dd",
    },
    "templated_report.updated_at": {
      data_type: "date",
      display_name: "Templated Report Updated At",
      format: "yyyy-MM-dd",
    },
    "current_version.report_id": {
      data_type: "string",
      display_name: "Current Version Report ID",
    },
    "current_version.version": {
      data_type: "number",
      display_name: "Current Version Version",
    },
    "current_version.is_current": {
      data_type: "boolean",
      display_name: "Current Version Is Current",
    },
    "current_version.s3_object_id": {
      data_type: "string",
      display_name: "S3 Object ID",
    },
    "current_version.definition_version": {
      data_type: "string",
      display_name: "Definition Version",
    },
    "current_version.status": {
      data_type: "pick-list",
      pick_list_options: Object.values(ReportGeneratedStatus),
      display_name: "Current Version Status",
    },
    "current_version.created_at": {
      data_type: "date",
      display_name: "Current Version Created At",
      format: "yyyy-MM-dd",
    },
    "current_version.updated_at": {
      data_type: "date",
      display_name: "Current Version Updated At",
      format: "yyyy-MM-dd",
    },
  };

const WorkflowConfigurationSendReportDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const { setSendReport, setSendReportIsValid, getSendReportFormValues } =
    useWorkflowConfigurationFormStore();
  const { getAvailableStepVariables } = useWorkflowGraphState();
  const availableVariables = getAvailableStepVariables(stepId ?? "");

  const storeValues = getSendReportFormValues();
  const workflowVariables = getAvailableStepVariables(stepId ?? "");

  const [mode, setMode] = useState<"by_report_filter" | "by_report_id">(
    "by_report_filter"
  );
  const [reportFilter, setReportFilter] = useState<Record<string, any>>(
    storeValues?.report_filter ?? {}
  );

  const setReportFilterBuilderFilters = (
    newFilterValue: FilterBuilderFilterType
  ) => {
    setReportFilter(newFilterValue);
    handleValueChange("report_filter", newFilterValue);
    if (Object.keys(newFilterValue).length > 0) {
      setSendReportIsValid(true);
    } else {
      setSendReportIsValid(false);
    }
  };

  const form = useForm<SendReportFormValuesType>({
    values: storeValues,
  });
  const formValues = form.watch();

  useDeepCompareEffectNoCheck(() => {
    if (!storeValues?.report_filter) return;
    const parseResult = parseBackendFilterToFilterBuilderFormat(
      storeValues.report_filter
    );
    setReportFilter(parseResult);
  }, [storeValues?.report_filter]);

  useEffect(() => {
    setSendReportIsValid(form.formState.isValid);
  }, [form.formState.isValid, setSendReportIsValid]);

  const handleValueChange = (
    key:
      | "title"
      | "message"
      | "users"
      | "groups"
      | "report_filter"
      | "report_id",
    value: unknown
  ) => {
    if (storeValues) {
      const finalValues = { ...storeValues, [key]: value };
      if (key === "report_id") {
        finalValues.report_filter = undefined;
      }
      if (key === "report_filter") {
        finalValues.report_id = undefined;
      }
      setSendReport(finalValues);
    }
  };

  useEffect(() => {
    if (storeValues?.report_filter) {
      setMode("by_report_filter");
    } else {
      setMode("by_report_id");
    }
  }, [storeValues]);

  const modeOptions = [
    { label: "By Report Filter", value: "by_report_filter" },
    { label: "By Report ID", value: "by_report_id" },
  ];
  const handleModeChange = (newMode: string) => {
    if (newMode === "by_report_id") {
      form.setValue("report_filter", undefined);
      form.setValue("report_id", "");
    } else {
      // Clear report_id when switching to template mode
      form.setValue("report_id", undefined);
      form.setValue("report_filter", {});
    }
    setMode(newMode as "by_report_filter" | "by_report_id");
  };

  return (
    <div className={cx("form")}>
      <Form {...form}>
        <div className={cx("section")}>
          <p className={cx("section-title")}>Report to Send</p>
          <Radio
            name="mode"
            options={modeOptions}
            value={mode}
            onChange={handleModeChange}
            labelKey="label"
            valueKey="value"
            style={{ marginBottom: 16 }}
          />
          {mode === "by_report_filter" && (
            <FilterBuilder
              filter={reportFilter}
              setFilter={setReportFilter}
              onFilterChange={setReportFilterBuilderFilters}
              fields={SEND_REPORT_ACTION_FILTER_BUILDER_FIELDS}
              title="Report Filter"
              variant="vertical"
            />
          )}
          {mode === "by_report_id" && (
            <DropdownInput
              name="report_id"
              label="Report ID"
              options={availableVariables.map((value) => ({
                label: value,
                value,
              }))}
              placeholder="Select Report ID Variable..."
              isRequired
              isFluid
              labelKey="label"
              valueKey="value"
              isSearchable
              onChange={(value) => handleValueChange("report_id", value)}
            />
          )}
        </div>
        <div className={cx("section")}>
          <p className={cx("section-title")}>Content</p>
          <VariableTextInput
            name="title"
            label="Subject"
            isRequired
            suggestions={workflowVariables}
            type="text"
            onChange={(value) => handleValueChange("title", value)}
          />
          <VariableTextInput
            name="message"
            label="Body"
            isRequired
            suggestions={workflowVariables}
            type="textarea"
            onChange={(value) => handleValueChange("message", value)}
          />
        </div>
        <div className={cx("section")}>
          <p className={cx("section-title")}>Recipients</p>
          <UsersDropdownInput
            name="users"
            label="Users"
            placeholder="Select one or more users..."
            isFluid
            isMulti
            isRequired={!formValues.groups?.length && !formValues.users?.length}
            onChange={(value) => handleValueChange("users", value)}
          />
          <UserGroupsDropdownInput
            name="groups"
            label="Groups"
            placeholder="Select one or more groups..."
            isFluid
            isMulti
            isRequired={!formValues.groups?.length && !formValues.users?.length}
            onChange={(value) => handleValueChange("groups", value)}
          />
        </div>
      </Form>
    </div>
  );
};

export default WorkflowConfigurationSendReportDetails;
